import { gsap } from "gsap";

class AnimationStagger {
  constructor() {
    this.initialOffset = 60;
    this.wordOffset = 15;

    this.containers = document.querySelectorAll(
      ".lc-keyfigures-box-container, .lc-rating-container, .lc-box-main-container, .lc-box-popup-main-container, .lc-lottie-background, .lc-swiper-new, .lc-slider"
    );

    if (!this.containers.length) return;

    this.prepareInitialStates();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          this.animateRvbBoxItems(entry.target);
          this.observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    this.containers.forEach((container) => {
      this.observer.observe(container);
    });
  }

  prepareInitialStates() {
    this.containers.forEach((container) => {
      if (container.matches(".lc-box-main-container")) {
        const infoboxes = container.querySelectorAll(".lc-infobox");

        infoboxes.forEach((infobox) => {
          const infoboxItems = infobox.querySelectorAll(".lc-infobox__col--text > *");

          if (infoboxItems.length) {
            gsap.set(infoboxItems, { y: this.initialOffset, opacity: 0 });
          }
        });

        return;
      }

      if (container.matches(".lc-box-popup-main-container")) {
        const popupTextColumns = container.querySelectorAll(".lc-infobox-popup__col--text");

        popupTextColumns.forEach((popupTextColumn) => {
          const popupItems = popupTextColumn.querySelectorAll(":scope > *");

          if (popupItems.length) {
            gsap.set(popupItems, { y: this.initialOffset, opacity: 0 });
          }
        });

        return;
      }

      if (container.matches(".lc-lottie-background")) {
        const quoteBlocks = container.querySelectorAll(
          ".lc-quote__text .lc-quote__quote, .lc-quote__text .lc-quote__authors"
        );

        quoteBlocks.forEach((quoteBlock) => {
          const words = this.getAnimatedWords(quoteBlock);

          if (words.length) {
            gsap.set(words, { y: this.wordOffset, opacity: 0 });
          }
        });

        return;
      }

      if (container.matches(".lc-swiper-new")) {
        const swiperTitle = container.querySelector(".swiper-new .lc-title-h2");
        const titleItems = swiperTitle?.querySelectorAll(":scope > *");

        if (titleItems?.length) {
          gsap.set(titleItems, { y: this.initialOffset, opacity: 0 });
        }

        return;
      }

      if (container.matches(".lc-slider")) {
        const sliderTitle = container.querySelector(
          ".lc-slider__main-title-container .lc-title-h2"
        );
        const titleItems = sliderTitle?.querySelectorAll(":scope > *");

        if (titleItems?.length) {
          gsap.set(titleItems, { y: this.initialOffset, opacity: 0 });
        }

        return;
      }

      const rvbItems = container.querySelectorAll(".rvbBox");

      if (rvbItems.length) {
        gsap.set(rvbItems, { y: this.initialOffset, opacity: 0 });
      }
    });
  }

  animateRvbBoxItems(container) {
    if (container.matches(".lc-lottie-background")) {
      const quoteBlocks = container.querySelectorAll(
        ".lc-quote__text .lc-quote__quote, .lc-quote__text .lc-quote__authors"
      );

      if (!quoteBlocks.length) return;

      const words = [];

      quoteBlocks.forEach((quoteBlock) => {
        words.push(...this.getAnimatedWords(quoteBlock));
      });

      if (!words.length) return;

      gsap.to(words, {
        y: 0,
        opacity: 1,
        stagger: 0.15,
        duration: 0.5,
        ease: "power2.out",
      });

      return;
    }

    if (container.matches(".lc-box-main-container")) {
      const infoboxes = container.querySelectorAll(".lc-infobox");

      if (!infoboxes.length) return;

      const textAnimationDelay = 0.2;

      infoboxes.forEach((infobox, index) => {
        const infoboxItems = infobox.querySelectorAll(
          ".lc-infobox__col--text > *"
        );

        if (!infoboxItems.length) return;

        gsap.to(infoboxItems, {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: "power2.out",
          stagger: 0.3,
          delay: textAnimationDelay + index * 0.3,
        });
      });

      return;
    }

    if (container.matches(".lc-box-popup-main-container")) {
      const popupTextColumns = container.querySelectorAll(
        ".lc-infobox-popup__col--text"
      );

      if (!popupTextColumns.length) return;

      const textAnimationDelay = 0.2;

      popupTextColumns.forEach((popupTextColumn, index) => {
        const popupItems = popupTextColumn.querySelectorAll(":scope > *");

        if (!popupItems.length) return;

        gsap.to(popupItems, {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: "power2.out",
          stagger: 0.3,
          delay: textAnimationDelay + index * 0.3,
        });
      });

      return;
    }

    if (container.matches(".lc-swiper-new")) {
      const swiperTitle = container.querySelector(".swiper-new .lc-title-h2");
      const titleItems = swiperTitle?.querySelectorAll(":scope > *");

      if (!titleItems?.length) return;

      gsap.to(titleItems, {
        y: 0,
        opacity: 1,
        duration: 0.9,
        ease: "power2.out",
        stagger: 0.3,
        delay: 0.2,
      });

      return;
    }

    if (container.matches(".lc-slider")) {
      const sliderTitle = container.querySelector(
        ".lc-slider__main-title-container .lc-title-h2"
      );
      const titleItems = sliderTitle?.querySelectorAll(":scope > *");

      if (!titleItems?.length) return;

      gsap.to(titleItems, {
        y: 0,
        opacity: 1,
        duration: 0.9,
        ease: "power2.out",
        stagger: 0.3,
        delay: 0.2,
      });

      return;
    }

    const rvbItems = container.querySelectorAll(".rvbBox");

    if (!rvbItems.length) return;

    gsap.to(rvbItems, {
      y: 0,
      opacity: 1,
      duration: 0.9,
      ease: "power2.out",
      stagger: 0.3,
    });
  }

  getAnimatedWords(element) {
    if (element.dataset.wordsSplit === "true") {
      return Array.from(element.querySelectorAll(".lc-quote-word"));
    }

    const text = (element.textContent || "").trim();

    if (!text) return [];

    const words = text.split(/\s+/).filter(Boolean);

    if (!words.length) return [];

    const fragment = document.createDocumentFragment();
    const wordSpans = [];

    words.forEach((word, index) => {
      const span = document.createElement("span");
      span.className = "lc-quote-word";
      span.style.display = "inline-block";
      span.textContent = word;
      fragment.appendChild(span);
      wordSpans.push(span);

      if (index < words.length - 1) {
        fragment.appendChild(document.createTextNode(" "));
      }
    });

    element.textContent = "";
    element.appendChild(fragment);
    element.dataset.wordsSplit = "true";

    return wordSpans;
  }
}

export default AnimationStagger;
